import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MetaTags } from "react-meta-tags";
import { useDispatch, useSelector } from 'react-redux';
import { getAnswerAgentProfile, getQuoteFunnelQuestions, getQuoteFunnelSessionId } from 'store/quote_funnels/answers/actions';
import Header from './Header';
import QuoteFunnelAnswer from '../QuoteFunnelAnswer';
import Footer from './Footer';
import LoadingSpinner from 'components/Common/LoadingSpinner';
import { useParams, useHistory } from 'react-router-dom';
import useQuery from 'hooks/useQuery';
import CookieConsent from 'react-cookie-consent';
import { QF_TRACKING_CODE_CLASS_NAMES, REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useIntervalWatcher from 'hooks/useIntervalWatcher';

const isBlank = (value) => {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

const QuoteFunnelAnswerLayout = () => {

    const dispatch = useDispatch();
    const history = useHistory();
    const query = useQuery();
    const { quote_funnel_id, session_id, campaign_id } = useParams()
    const meta = query.get('meta');
    const review = parseInt(query.get('review') || 0);
    const preview = parseInt(query.get('preview') || 0);
    const brand_id = query.get('brand_id');
    const page_group_current_index = parseInt(query.get('page_group_current_index') || 0);
    const page_current_index = parseInt(query.get('page_current_index') || 0);

    const [isFeatureFlagActive] = useFeatureFlags();
    const new_consent_ui_ff = isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ENABLE_CUSTOM_CONSENT_UI)

    const initial_params = {
        session_id: session_id,
        quote_funnel_id: quote_funnel_id,
        campaign_id: campaign_id,
        preview: preview,
        brand_id: brand_id,
    }
    const [isCalifornia, setIsCalifornia] = useState(false)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const trustedFormToken = useIntervalWatcher(100, { initialValue: null, name: "trustedFormToken" }, () => {
        const trustedFormToken = document.getElementsByName('xxTrustedFormToken')?.[0]?.value;
        return { value: trustedFormToken, done: !isBlank(trustedFormToken) }
    });

    const leadidToken = useIntervalWatcher(100, { initialValue: null, name: "leadidToken" }, () => {
        const leadidToken = document.getElementById('leadid_token')?.value;
        return { value: leadidToken, done: !isBlank(leadidToken) }
    });

    const consentCertificateId = useIntervalWatcher(100, { initialValue: null, name: "consentCertificateId" }, () => {
        const consentCertificateId = document.querySelector('input[name="consent-confirmation-certificate-id"]')?.value;
        return { value: consentCertificateId, done: !isBlank(consentCertificateId) }
    });

    const cookieConsentRef = useRef();

    const handleButtonClick = () => {
        cookieConsentRef.current.accept();
    };

    useEffect(() => {
        const updateIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', updateIsMobile);
        return () => {
            window.removeEventListener('resize', updateIsMobile);
        };
    }, []);

    // Legacy code to be removed once useIntervalWatcher is validated to be working well
    // FIXME: GOAL-31 mouseover does't work when we're scrolling on mobile
    // This can cause the certicate to get created on consentconfirmation
    // but not being saved in Goal system
    // window.addEventListener('mouseover', function (event) {
    //     const newTrustedFormToken = document.getElementsByName('xxTrustedFormToken');
    //     const newLeadidToken = document.getElementById('leadid_token');
    //     const newConsentCertificateId = document.querySelector('input[name="consent-confirmation-certificate-id"]')
    //     if (!!newLeadidToken?.value) {
    //         setLeadidToken(newLeadidToken?.value || "");
    //     }

    //     if (!!newConsentCertificateId?.value) {
    //         setConsentCertificateId(newConsentCertificateId?.value)
    //     }

    //     if (newTrustedFormToken?.[0]?.value && !!leadidToken) {
    //         setTrustedFormToken(newTrustedFormToken?.[0]?.value);
    //     }
    // });

    const {
        answer_agent_profile,
        answer_agent_profile_loading,
        quote_funnel_questions,
        quote_funnel_questions_loading,
        quote_form_visit
    } = useSelector((state) => ({
        answer_agent_profile: state.QuoteFunnelAnswersAgentProfileReducer.answer_agent_profile,
        answer_agent_profile_loading: state.QuoteFunnelAnswersAgentProfileReducer.answer_agent_profile_loading,
        quote_funnel_questions: state.QuoteFunnelAnswersAgentProfileReducer.quote_funnel_questions,
        quote_funnel_questions_loading: state.QuoteFunnelAnswersAgentProfileReducer.quote_funnel_questions_loading,
        quote_form_visit: state.QuoteFunnelAnswersAgentProfileReducer.quote_form_visit
    }));

    useEffect(() => {
        // Create a unique identifier
        const addedHeadElements = document.head.getElementsByClassName(QF_TRACKING_CODE_CLASS_NAMES.HEAD);
        const addedHeadElementsArr = Array.from(addedHeadElements)
        // Dynamically adding HTML to the head
        if (!addedHeadElementsArr.length && quote_funnel_questions?.form_header_tracking_code) {
            const head = document.head;
            const headTempDiv = document.createElement('div');
            headTempDiv.innerHTML = quote_funnel_questions?.form_header_tracking_code;
            // Append each child node to the head
            while (headTempDiv.firstChild) {
                headTempDiv.firstChild.className =
                    headTempDiv.firstChild.className ? headTempDiv.firstChild.className + " " + QF_TRACKING_CODE_CLASS_NAMES.HEAD :
                        QF_TRACKING_CODE_CLASS_NAMES.HEAD
                head.appendChild(headTempDiv.firstChild);
            }
        }
        // Create a unique identifier
        const addedBodyElements = document.body.getElementsByClassName(QF_TRACKING_CODE_CLASS_NAMES.BODY);
        const addedBodyElementsArr = Array.from(addedBodyElements)
        // Dynamically adding HTML to the body
        if (!addedBodyElementsArr.length && quote_funnel_questions?.form_body_tracking_code) {
            const body = document.body;
            const bodyTempDiv = document.createElement('div');
            bodyTempDiv.innerHTML = quote_funnel_questions?.form_body_tracking_code;

            // Append each child node to the head
            while (bodyTempDiv.firstChild) {
                bodyTempDiv.firstChild.className =
                    bodyTempDiv.firstChild.className ? bodyTempDiv.firstChild.className + " " + QF_TRACKING_CODE_CLASS_NAMES.BODY :
                        QF_TRACKING_CODE_CLASS_NAMES.BODY
                body.appendChild(bodyTempDiv.firstChild);
            }
        }
    }, [quote_funnel_questions]); // Empty dependency array ensures that the effect runs only once on mount    

    useEffect(() => {
        // Clean up function to remove the added HTML when the component unmounts
        return () => {
            const addedHeadElements = document.head.getElementsByClassName(QF_TRACKING_CODE_CLASS_NAMES.HEAD);
            const addedHeadElementsArr = Array.from(addedHeadElements)
            addedHeadElementsArr.forEach(element => {
                element.remove()
            })

            const addedBodyElements = document.body.getElementsByClassName(QF_TRACKING_CODE_CLASS_NAMES.BODY);
            const addedBodyElementsArr = Array.from(addedBodyElements)
            addedBodyElementsArr.forEach(element => {
                element.remove()
            })
        };
    }, [])

    useEffect(() => {
        const isTrustedFormTokenPresent = !!trustedFormToken;
        const isLeadidTokenPresent = !!leadidToken;
        const isQuoteFormVisitPresent = !!quote_form_visit;
        const isTrustedFormTokenAbsentInQuoteFormVisit = isQuoteFormVisitPresent && !quote_form_visit.trusted_form_token;
        const isJornayaLeadIdAbsentInQuoteFormVisit = isQuoteFormVisitPresent && !quote_form_visit.jornaya_lead_id;
        const isConsentCertificateIdPresent = !!consentCertificateId;
        const isConsentCertificateIdAbsentInQuoteFormVisit = isQuoteFormVisitPresent && !quote_form_visit.consent_certificate_id;

        const isConditionMet =
            isTrustedFormTokenPresent &&
            isLeadidTokenPresent &&
            isQuoteFormVisitPresent &&
            isTrustedFormTokenAbsentInQuoteFormVisit &&
            isJornayaLeadIdAbsentInQuoteFormVisit &&
            (new_consent_ui_ff ? isConsentCertificateIdPresent && isConsentCertificateIdAbsentInQuoteFormVisit : true);

        if (isConditionMet) {
            const token = trustedFormToken.split("/").pop();
            const data = {
                ...initial_params,
                update_visit: 1,
                reducer_update_not_required: true,
                quote_form_visit_id: quote_form_visit?.id,
                trusted_form_token: token,
                jornaya_lead_id: leadidToken,
            }
            if (new_consent_ui_ff) {
                data.consent_certificate_id = consentCertificateId
            }
            dispatch(getQuoteFunnelQuestions(data));
        }
        if (quote_form_visit?.region?.toLowerCase() === 'california') {
            setIsCalifornia(true)
        }
    }, [trustedFormToken, leadidToken, quote_form_visit, consentCertificateId]);

    useEffect(() => {
        if (!session_id || session_id === '') {
            const data = {
                ...initial_params,
                preview: initial_params.preview ? true : false
            }
            dispatch(getQuoteFunnelSessionId(data, history));
        } else {
            dispatch(getAnswerAgentProfile(initial_params));
            const data = {
                ...initial_params,
                page_group_index: page_group_current_index,
                page_index: page_current_index
            }
            if (quote_form_visit) {
                data.quote_form_visit_id = quote_form_visit.id
            } else {
                if (!(preview || review)) {
                    data.create_visit = 1;
                }
            }
            dispatch(getQuoteFunnelQuestions(data));
        }
    }, [session_id]);

    const sorted_quote_funnel_questions = useMemo(() => {
        if (quote_funnel_questions) {
            return {
                ...quote_funnel_questions,
                page_groups: (quote_funnel_questions?.page_groups || []).sort((a, b) => a.order - b.order),
            };
        }

        return null;
    }, [quote_funnel_questions]);

    if (answer_agent_profile_loading || !session_id) {
        return <LoadingSpinner />
    }
    let page_title = !answer_agent_profile?.name ? '' : (answer_agent_profile?.name + " | " + quote_funnel_questions?.name)
    return (
        <div className='quote-funnel-div-wrapper'>
            <div className="page-content quote-funnel-div" style={{ padding: '0px' }} >
                <React.Fragment>
                    <MetaTags>
                        <title>{page_title} </title>
                    </MetaTags>
                    <Header
                        answer_agent_profile={answer_agent_profile}
                        progress_percentage={sorted_quote_funnel_questions?.prefill_perc || 0}
                        lead_type_id={sorted_quote_funnel_questions?.lead_type_id || null}
                        tracking_number={sorted_quote_funnel_questions?.tracking_number}
                        stepper={true}
                        phone_number_text={preview === 1 ? answer_agent_profile?.phone_num : null}
                    />
                    <form>
                        {sorted_quote_funnel_questions && (
                            <QuoteFunnelAnswer
                                quote_funnel_questions={sorted_quote_funnel_questions}
                                quote_funnel_questions_loading={quote_funnel_questions_loading}
                                session_id={session_id}
                                meta={meta}
                                quote_form_visit={quote_form_visit}
                                tcpa_disclaimer_text={answer_agent_profile?.tcpa_content}
                            />
                        )}
                    </form>
                    {isCalifornia && <CookieConsent
                        ref={cookieConsentRef}
                        disableStyles={true}
                        location="bottom"
                        buttonText="x"
                        cookieName="californiaCookie"
                        style={
                            {
                                background: "#fff",
                                borderTop: 'inset',
                                borderTopWidth: '0.5px',
                                position: 'fixed',
                                bottom: '0',
                            }
                        }
                        buttonStyle={{ display: 'none' }}
                        expires={1}
                    >
                        <div
                            className={`p-2 d-flex ${isMobile ? 'flex-column-reverse' : 'flex-row'}`}
                        >
                            <div
                                className='text-muted'
                                style={{
                                    fontSize: isMobile ? '12px' : '13px'
                                }}>

                                Our website utilizes cookies and comparable technologies to
                                gather essential data required for the proper functioning of
                                our services. This includes processing your requests,
                                personalizing your browsing experience, and assessing
                                the efficiency of the content displayed, among other necessary
                                business objectives. We may share this data with the business
                                partners we collaborate with, who may combine it with information
                                you have provided them or that they have collected from you.
                                For further information on cookies and how to disable them,
                                kindly refer to our <a href='/privacy-policy' className='text-sfblue' target='_blank'> Privacy Policy</a>.
                            </div>
                            <button
                                type="button"
                                className="btn btn-secondary rounded-circle ms-auto"
                                style={{
                                    height: '30px',
                                    width: '30px',
                                    aspectRatio: '1 / 1',
                                    padding: 'initial'
                                }}
                                onClick={handleButtonClick}
                            >
                                <span aria-hidden="true" className='fs-20 text-white'>&times;</span>
                            </button>
                        </div>

                    </CookieConsent>
                    }
                    <Footer
                        answer_agent_profile={answer_agent_profile}
                        disclaimer={true}
                        stepper={true} tracking_number={quote_funnel_questions?.tracking_number}
                        phone_number_text={preview === 1 ? answer_agent_profile?.phone_num : null}
                        tcpa_disclaimer_text={answer_agent_profile?.tcpa_content}
                    />
                </React.Fragment>
            </div>
        </div>
    );
};

export default QuoteFunnelAnswerLayout;
